<template>
  <ValidationObserver
    v-slot="{ handleSubmit }"
    ref="mergeProcessObserver"
    tag="section"
    :class="{ 'spacing-px-20': !isMobile }"
  >
    <b-loading isFullPage :active="isLoading" />
    <h2 class="is-size-5 has-text-weight-bold">
      {{ General.TEXTS.JOINT_BY_APPEND }}
    </h2>
    <div class="columns spacing-mt-15 mb-5">
      <ValidationProvider
        v-slot="{ errors }"
        rules="required"
        class="column is-5"
      >
        <b-field
          :label="General.TEXTS.PROCESS_TO_APPEND"
          customClass="has-text-color-8"
          :type="isContrast ? 'is-white' : errors.length ? 'is-refused' : ''"
        >
          <b-autocomplete
            v-model="nup"
            v-mask="'#####.######/####-##'"
            :data="processListFiltered"
            field="nupMasked"
            placeholder="00000.000000/0000-00"
            openOnFocus
            keepFirst
            role="combobox"
            @typing="typeNUP"
            @select="getProcessReference"
          >
            <template slot-scope="props">
              <div class="has-text-weight-bold">
                {{ `NUP: ${props.option.nupMasked}` }}
              </div>
              <span class="is-size-7">
                {{ `Assunto: ${props.option.subject_name}` }}
              </span>
            </template>
          </b-autocomplete>
          <template #message>
            <span v-if="!processListFiltered.length" class="has-text-danger">
              {{ General.APPEND_WITHOUT_PROCESS }}</span
            >

            <ErrorField v-else :errors="errors" />
          </template>
        </b-field>
      </ValidationProvider>
      <div v-if="nup && getProcessSimple" class="column is-7 process-selected">
        <div class="is-flex is-flex-direction-row is-align-items-center">
          <b-icon
            v-if="!isMobile"
            class="spacing-mx-10"
            type="is-primary"
            icon="alert-circle-outline"
            size="is-small"
          />
          <div>
            <p>
              <span class="has-text-weight-semibold">{{
                `${General.NUP} ${applyMaskNUP(getProcessSimple.nup)}`
              }}</span>
              <span class="spacing-mx-20">
                {{ General.OPENING_DATE }}:
                {{
                  new Date(getProcessSimple.date) | dateFormat("DD/MM/YYYY")
                }}</span
              >
            </p>
            <p>
              <span class="has-text-weight-semibold"
                >{{ General.SUBJECT }}:
              </span>
              <span class="is-capitalized">
                {{ getProcessSimple.subject.toLowerCase() }}
              </span>
            </p>
            <p>
              <span class="has-text-weight-semibold"
                >{{ General.OPENING_UNIT }}:
              </span>
              <span class="is-capitalized">
                {{
                  `${getProcessSimple.origin.entity.abbreviation}/${
                    getProcessSimple.origin.abbreviation.includes("/")
                      ? getProcessSimple.origin.abbreviation.split("/")[1]
                      : getProcessSimple.origin.abbreviation
                  } - ${getProcessSimple.origin.description.toLowerCase()}`
                }}
              </span>
            </p>
            <p v-if="getProcessSimple.cc.length">
              <span class="has-text-weight-semibold">
                {{ General.INTERESTED }}:
              </span>
              {{
                getProcessSimple.cc[0].user_name
                  ? getProcessSimple.cc[0].user_name
                  : `${getProcessSimple.cc[0].entity.abbreviation}/${
                      getProcessSimple.cc[0].abbreviation.includes("/")
                        ? getProcessSimple.cc[0].abbreviation.split("/")[1]
                        : getProcessSimple.cc[0].abbreviation
                    } - ${getProcessSimple.cc[0].description}`
              }}
            </p>
          </div>
        </div>
        <div class="icons-container">
          <b-tooltip :label="General.TO_VIEW" type="is-blue-oil" role="tooltip">
            <a @click="getCompleteProcessFileStream(getProcessSimple.id)">
              <b-icon icon="magnify-plus" type="is-black" />
            </a>
          </b-tooltip>
        </div>
        <b-tooltip
          id="close-button"
          :label="General.REMOVE"
          type="is-blue-oil"
          role="tooltip"
        >
          <a @click="removeProcessReference">
            <b-icon icon="close" type="is-black" />
          </a>
        </b-tooltip>
      </div>
    </div>
    <ValidationProvider v-slot="{ errors }" rules="required">
      <b-field
        :label="General.JUSTIFICATION"
        customClass="has-text-color-7"
        :type="isContrast ? 'is-white' : errors.length ? 'is-refused' : ''"
      >
        <b-input
          v-model="motivation"
          maxlength="200"
          type="textarea"
          role="input"
        />
        <template #message>
          <ErrorField :errors="errors" />
        </template>
      </b-field>
    </ValidationProvider>
    <div class="align-right spacing-mb-30 mt-20">
      <b-button
        :type="isContrast ? 'is-white' : 'is-primary'"
        :expanded="isMobile"
        role="button"
        @click="handleSubmit(submitAppend)"
      >
        <span>
          {{ General.SAVE.toUpperCase() }}
        </span>
      </b-button>
    </div>
    <JointList :processId="processId" />
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import { App, General } from "@/utils/constants";
import { PROCESS_DETAILS_TABS as TABS } from "@/utils/enumerate";
import { NUP, applyMask } from "@/utils/protocols";
import JointList from "@/components/JointList";
import { showErrorMessage } from "@/utils/messageUtility";
import { debounce } from "lodash";

export default {
  name: "MergeProcessTab",
  components: {
    JointList
  },

  data() {
    return {
      General,
      TABS,
      isLoading: false,
      nup: null,
      motivation: "",
      filterNUP: ""
    };
  },

  computed: {
    ...mapGetters({
      getProcessSimple: "getProcessSimple",
      process: "getCurrentProcess",
      user: "getCurrentUser",
      getCurrentTab: "getCurrentTabProcessDetails",
      getAppendedProcesses: "getAppendedProcesses",
      getSimpleMyProcessList: "getSimpleMyProcessList",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    processListFiltered() {
      const processList = this.getSimpleMyProcessList
        .filter(process => process.nup !== this.process.nup)
        .map(process => {
          return {
            ...process,
            nupMasked: this.applyMaskNUP(process.nup)
          };
        });

      return processList;
    },

    processId() {
      return this.$route.params.id;
    }
  },

  async beforeMount() {
    this.isLoading = true;
    this.setReferenceProcess(null);
    this.isLoading = false;
  },

  beforeDestroy() {
    this.setSimpleProcess(null);
    this.setAppendedProcesses([]);
    this.resetSimpleMyProcessList();
  },

  methods: {
    ...mapMutations({
      setReferenceProcess: "setReferenceProcess",
      setAppendedProcesses: "setAppendedProcesses",
      resetSimpleMyProcessList: "resetSimpleMyProcessList",
      setSimpleProcess: "setSimpleProcess"
    }),
    ...mapActions({
      requestProcessByNupSimple: "requestProcessByNupSimple",
      getProcessAppended: "getProcessAppended",
      setProcessAppend: "setProcessAppend",
      getCompleteProcessFileStream: "getCompleteProcessFileStream",
      simpleMyProcessListRequest: "simpleMyProcessListRequest"
    }),

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    typeNUP: debounce(async function(nupValue) {
      const nupSanitized = nupValue.replace(/\D/g, "");
      const fullNUP = nupSanitized.length === 17;

      if (nupSanitized === this.process.nup)
        return showErrorMessage(General.REQUIRED_DIFFERENCE_NUP);

      await this.simpleMyProcessListRequest({
        page: 1,
        paginator_count: 5,
        search: nupSanitized
      });

      if (nupSanitized.length && !this.processListFiltered.length) {
        showErrorMessage(
          fullNUP ? App.MergeProcess.NUP_NOT_FOUND : General.NO_RESULTS
        );
      }
    }, 500),

    getProcessReference(item) {
      if (item) {
        const nupSanitized = item.nup.replace(/\D/g, "");
        const fullNUP = nupSanitized.length === 17;

        if (fullNUP) {
          this.requestProcessByNupSimple(nupSanitized);
        }
      }
    },

    removeProcessReference() {
      this.setReferenceProcess(null);
      this.nup = "";
      this.$refs.mergeProcessObserver.reset();
    },

    isValid() {
      const errors = [];

      if (!this.getProcessSimple) {
        errors.push(General.REQUIRED_PROCESS_TO_APPEND);
      } else if (
        this.process.user_responsible_id !== this.user.id ||
        this.getProcessSimple.user_responsible_id !== this.user.id ||
        this.processId === this.getProcessSimple.id
      )
        errors.push(General.REQUIRED_USER_RESPONSABLE);
      if (!this.motivation) errors.push(General.REASON_APPEND);

      if (errors.length)
        this.snackToast.open({
          message: `${errors.join(", ")}.`,
          type: "is-danger",
          duration: 8000
        });

      return !errors.length;
    },

    async submitAppend() {
      if (this.isValid()) {
        this.isLoading = true;
        const status = await this.setProcessAppend({
          processId: this.getProcessSimple.id,
          processAppend: {
            process_join_id: this.processId,
            process_join_description: this.motivation
          }
        });
        this.setReferenceProcess(null);
        await this.getProcessAppended(this.processId);
        if (status) {
          this.snackToast.open({
            message: this.General.MESSAGES.APPENDED_PROCESS_CONFIRM.replace(
              "(NUP)",
              this.nup
            ),
            type: "is-success",
            duration: 8000
          });
        }
        this.nup = "";
        this.motivation = "";
        this.$refs.mergeProcessObserver.reset();
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.wrapper-list {
  width: 100%;
  height: 120px;
  overflow: auto;
}
.align-right {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}

.custom-text-overflow {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-text-overflow.text-overflow-25 {
  max-width: 25%;
}

.custom-text-overflow.text-overflow-50 {
  max-width: 50%;
}

.process-selected {
  display: flex;
  flex: 1;
  margin-right: 10px;
  border-radius: 5px;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  background-color: $white-ter;
  color: $custom-color-2f3133;

  @media screen and (max-width: $tablet) {
    margin-left: 10px;
  }

  .icons-container {
    height: 100%;
    display: flex;
    align-self: center;
    align-items: center;
    margin: 0px 10px 0px auto;

    @media screen and (max-width: $tablet) {
      align-self: flex-start;
    }
  }
}

.process-list {
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  padding: 10px;
  justify-content: space-between;
  border-top: 1px solid $grey-lighter;
  border-bottom: 1px solid $grey-lighter;

  .process-info {
    flex: 1;
    cursor: pointer;
  }
}
</style>
